import { useState } from 'react'
import GridItemActionsMenu from './GridItemActionsMenu'
import RollingPrice from './RollingPrice'
import ProductImage from './ProductImage'
import { itemsService } from '../services/api'
import { readItemGallery, resolveCardImage } from '../utils/itemMeta'
import { displayItemLink, displayItemName } from '../utils/itemPresentation'
import { sanitizeUrl } from '../utils/sanitize'
import '../pages/CartDetailPage.css'

const DEFAULT_INCLUDE = ['open', 'edit', 'delete']

export default function ProductItemCard({
  item,
  viewMode = 'list',
  emptyLinkLabel = 'Lien du produit',
  includeActions = DEFAULT_INCLUDE,
  onEdit,
  onDelete,
  onOpenLink,
  onImageClick,
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [menuArrow, setMenuArrow] = useState('down')
  const resolved = resolveCardImage(item)
  const ready = Boolean(resolved.url || resolved.key)
  const title = item?.name || 'Produit sans nom'
  const shownName = displayItemName(title)
  const shownLink = displayItemLink(item?.link)
  const actions = item?.link ? includeActions : includeActions.filter((id) => id !== 'open')

  const openLink = () => {
    if (onOpenLink) {
      onOpenLink(item)
      return
    }
    const safeUrl = sanitizeUrl(item?.link)
    if (safeUrl) window.open(safeUrl, '_blank', 'noopener,noreferrer')
  }

  const runAction = (id) => {
    if (id === 'open') openLink()
    if (id === 'edit') onEdit?.(item)
    if (id === 'delete') onDelete?.(item)
  }

  return (
    <div
      className="item-card glass-container"
      data-item-id={item?.id}
      onDoubleClick={(event) => {
        const target = event.target
        if (target.closest?.('button') || target.closest?.('.item-buttons') || target.closest?.('.btn-icon-small')) {
          return
        }
        onEdit?.(item)
      }}
    >
      <div className="glass-filter"></div>
      <div className="glass-overlay"></div>
      <div className="glass-specular"></div>
      <div className="glass-content">
        <div className="item-image" style={{ cursor: ready ? 'pointer' : 'default' }}>
          <ProductImage
            item={item}
            alt={title}
            style={{ pointerEvents: 'auto', cursor: ready ? 'pointer' : 'default' }}
            onClick={async (event) => {
              event.preventDefault()
              event.stopPropagation()
              const current = resolveCardImage(item)
              let next = current.url || readItemGallery(item).cardUrl
              if (!next && current.key) {
                next = await itemsService.signedMedia(current.key)
              }
              if (next) onImageClick?.(next)
            }}
          />
        </div>
        <div className="item-content">
          <div className="item-name" title={title}>{shownName}</div>
          <div
            className="item-link"
            title={item?.link || ''}
            style={{ color: shownLink ? 'inherit' : 'rgba(255, 255, 255, 0.5)' }}
          >
            {shownLink || emptyLinkLabel}
          </div>
          <div className="item-badges" style={item?.promo ? undefined : { display: 'none' }}>
            {item?.promo ? <span className="badge badge-promo">{item.promo}</span> : null}
          </div>
        </div>
        <div className="item-actions">
          <div className="item-price-qty">
            <div className="item-price-block">
              <span className="item-metric-label">Prix</span>
              <div
                className="price-input"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  minHeight: '32px',
                  color: 'inherit',
                }}
              >
                <RollingPrice value={item?.price || 0} />
              </div>
            </div>
            <div className="item-qty-block">
              <span className="item-metric-label">Qté</span>
              <div
                className="qty-input"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  minHeight: '32px',
                  color: 'inherit',
                }}
              >
                {item?.quantity || 1}
              </div>
            </div>
          </div>
          {viewMode === 'grid' ? (
            <div className="item-buttons grid-actions-menu">
              <button
                type="button"
                className="btn-icon-small grid-menu-trigger grid-hamburger-container"
                aria-label="Actions"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                onClick={(event) => {
                  event.stopPropagation()
                  if (menuOpen) {
                    setMenuOpen(false)
                    return
                  }
                  setMenuAnchor(event.currentTarget)
                  setMenuArrow('down')
                  setMenuOpen(true)
                }}
              >
                <span
                  className={`grid-hamburger-label${menuOpen ? ` is-open is-dir-${menuArrow}` : ''}`}
                  aria-hidden="true"
                >
                  <span className="line1" />
                  <span className="line2" />
                  <span className="line3" />
                </span>
              </button>
              <GridItemActionsMenu
                isOpen={menuOpen}
                anchor={menuAnchor}
                item={item}
                include={actions}
                onClose={() => setMenuOpen(false)}
                onAction={runAction}
                onPlacementChange={setMenuArrow}
              />
            </div>
          ) : (
            <div className="item-buttons">
              {actions.includes('open') ? (
                <button type="button" className="btn-icon-small" onClick={openLink} title="Ouvrir le lien">
                  <img src="/Icon chrome.png" alt="Ouvrir" />
                </button>
              ) : null}
              {actions.includes('edit') ? (
                <button type="button" className="btn-icon-small" onClick={() => onEdit?.(item)} title="Modifier">
                  <img src="/Icon drive file rename outline.png" alt="Modifier" />
                </button>
              ) : null}
              {actions.includes('delete') ? (
                <button type="button" className="btn-icon-small" onClick={() => onDelete?.(item)} title="Supprimer">
                  <img src="/Icon trash.png" alt="Supprimer" />
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
